import { useRef } from "react";

/** The keys Base UI's non-native controls treat as activation, and synthesize a click for. */
const ACTIVATION_KEYS = new Set(["Enter", " "]);

function isHiddenControlInput(target: EventTarget): boolean {
  return target instanceof HTMLInputElement && target.type === "checkbox";
}

/**
 * Base UI's Switch and Checkbox roots render a non-native `<span role="switch|checkbox">` next to
 * a hidden `<input type="checkbox">`. On activation the root prevents the default on the real
 * click and re-dispatches a bubbling one from the input, so a pointer press sends *two* clicks
 * past our wrapper and an ancestor with its own `onClick` (a clickable row, say) runs twice per
 * press.
 *
 * Keyboard activation works differently: the root never receives a real click at all — Base UI
 * calls its own click handler straight out of `keydown`/`keyup` — so the re-dispatched click is
 * the only one there is. Dropping it unconditionally would take an ancestor from two clicks to
 * none for keyboard users.
 *
 * So track which modality opened the activation and drop the re-dispatched click only when a real
 * one is coming behind it. The state change rides the input's `change` event either way, so
 * containing the click costs the control nothing. Spread the result onto the wrapper element
 * between the Base UI root and the consumer's tree.
 *
 * Note this contains the duplicate for React `onClick` ancestors, which is how the design system's
 * clickable rows are written. An ancestor that listens with `addEventListener("click", ...)` sits
 * below React's root container and still sees both.
 *
 * This holds only while the root's host stays non-native. A native `<button>` host keeps the
 * browser's own click for Enter and Space, and the root re-dispatches from its input regardless,
 * so keyboard activation would produce two clicks with no way to tell them apart from here. The
 * wrappers therefore withhold `render` and `nativeButton` rather than have this hook guess at the
 * host it is not attached to.
 */
export function useOneClickPerActivation() {
  const fromKeyboard = useRef(false);

  return {
    // Both handlers capture, so they land before the Base UI root re-dispatches from its input.
    onPointerDownCapture: () => {
      fromKeyboard.current = false;
    },
    onKeyDownCapture: (event: React.KeyboardEvent) => {
      if (ACTIVATION_KEYS.has(event.key)) {
        fromKeyboard.current = true;
      }
    },
    onClick: (event: React.MouseEvent) => {
      if (isHiddenControlInput(event.target) && !fromKeyboard.current) {
        event.stopPropagation();
      }
      // One activation only ever produces one re-dispatch, so close the window behind it.
      fromKeyboard.current = false;
    },
  };
}
